import type { CourtFeed } from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import type { MatchLocator } from '@/app/[locale]/event/[eventId]/libs/matchLocators';
import type { MatchPhase } from '@/app/[locale]/event/[eventId]/libs/matchCardStatus';
import { embedHostAllowed } from '@/app/[locale]/event/[eventId]/libs/streamLinks';
import { playStart } from '@/app/[locale]/event/[eventId]/libs/watchMatches';

/** Where this match sits inside a public app recording: the feed and the second it starts. */
export type ScorecardReplay = { feed: CourtFeed; startSec: number };

/**
 * The scorecard's video, one of four cases: `live` plays the court while the match is on,
 * `replay` plays the match's recording from where the match starts, `link` opens a feed we may
 * not embed (WorldSquash, SquashTV and the like) on its own site, `none` shows nothing.
 */
export type ScorecardVideo =
  | { kind: 'live'; feeds: CourtFeed[] }
  | { kind: 'replay'; feed: CourtFeed; startSec: number }
  | { kind: 'link'; feed: CourtFeed }
  | { kind: 'none' };

/**
 * The match's recording, by the same locator and start second as Watch's `video` row. Null when
 * the match has no locator or its feed is not among the public ones. Pure.
 */
export function matchReplay(
  feeds: { feeds: CourtFeed[]; locators: MatchLocator[] },
  matchId: string,
): ScorecardReplay | null {
  const locator = feeds.locators.find((l) => l.matchId === matchId);
  const feed = locator ? feeds.feeds.find((f) => f.id === locator.feedId) : undefined;
  if (!locator || !feed) return null;
  const feedLive = feed.source === 'app' && feed.appState === 'live';
  return { feed, startSec: playStart(locator.offsetSec, feedLive) };
}

/** `StreamPlayer` puts a feed in an iframe only on these terms; anything else is a link card. */
const embeddable = (feed: CourtFeed) =>
  feed.display === 'inline' && !!feed.embedUrl && embedHostAllowed(feed.embedUrl);

/**
 * Which of the four cases the scorecard shows. Live: the court's feeds when one can play inline,
 * those first. Finished: the recording, when it can play inline. A pasted video with no recording
 * is `none`: nothing says where in it the match starts. Either way a feed we cannot embed is the
 * `link` fallback. Upcoming: `none`.
 * `courtFeeds` is the match's court on its day (`matchFeeds`). Pure.
 */
export function scorecardVideo(
  phase: MatchPhase,
  courtFeeds: CourtFeed[],
  replay: ScorecardReplay | null,
): ScorecardVideo {
  if (phase === 'upcoming') return { kind: 'none' };
  if (phase === 'live' && courtFeeds.some(embeddable)) {
    // The player opens on the first feed, so the ones it can play come first.
    const feeds = [...courtFeeds.filter(embeddable), ...courtFeeds.filter((f) => !embeddable(f))];
    return { kind: 'live', feeds };
  }
  if (phase === 'finished' && replay && embeddable(replay.feed))
    return { kind: 'replay', ...replay };
  const link = courtFeeds.find((feed) => !embeddable(feed));
  return link ? { kind: 'link', feed: link } : { kind: 'none' };
}
